import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchTaskTimerV2 } from '@/entities/task/api/tasks-v2';
import { useActiveTimerV2Store } from '@/entities/task/model/stores/active-timer-v2';
import { useTaskTimerStore } from '@/entities/task/model/stores/task-timer';
import { taskDetailPath } from '@/entities/task/model/task-route';
import { tasksV2Keys, useTaskV2 } from '@/entities/task/model/useTasksV2';
import { useActiveTimerTask } from '@/features/tasks/model/useActiveTimerTask';
import { formatHMS } from '@/shared/utils/helpers';
import { useIsNativeSession } from '@/store/auth';

import { useSidebarCollapsed } from './sidebarCollapse';
import { SidebarTooltip } from './SidebarTooltip';

const V2_STATUS_POLL_MS = 60_000;

function RunningIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      className="shrink-0"
    >
      <path d="M2 1.2L8.3 5 2 8.8V1.2Z" fill="#57B792" />
    </svg>
  );
}

function useSecondTick(enabled: boolean) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!enabled) {
      return;
    }
    const id = window.setInterval(() => setTick((x) => x + 1), 1000);
    return () => window.clearInterval(id);
  }, [enabled]);
}

function ActiveTimerRow({
  taskId,
  title,
  elapsedSeconds,
}: {
  taskId: string;
  title: string;
  elapsedSeconds: number;
}) {
  const navigate = useNavigate();
  const collapsed = useSidebarCollapsed();

  // Свёрнутый сайдбар: значок «идёт таймер», задача и время — в подсказке.
  if (collapsed) {
    return (
      <SidebarTooltip label={`${title} · ${formatHMS(elapsedSeconds)}`}>
        <button
          type="button"
          onClick={() => navigate(taskDetailPath(taskId))}
          aria-label={`Идёт таймер: ${title}`}
          className="mx-auto flex size-[40px] items-center justify-center rounded-[10px] bg-[#F6FBF9] transition-colors hover:bg-[#EDF7F3]"
        >
          <RunningIcon />
        </button>
      </SidebarTooltip>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate(taskDetailPath(taskId))}
      title={title}
      className="flex w-full items-center gap-[8px] rounded-[10px] bg-[#F6FBF9] px-[10px] py-[7px] text-left transition-colors hover:bg-[#EDF7F3]"
    >
      <RunningIcon />
      <span className="min-w-0 flex-1 truncate text-[12px] font-medium leading-[130%] text-[#313846]">
        {title}
      </span>
      <span className="shrink-0 text-[11px] font-normal tabular-nums text-[#57B792]">
        {formatHMS(elapsedSeconds)}
      </span>
    </button>
  );
}

// Счётчик текущего запуска таймера, а не общее время по задаче.
export function DesktopSidebarActiveTimer() {
  const isNativeSession = useIsNativeSession();

  return isNativeSession ? <ActiveTimerV2 /> : <ActiveTimerLegacy />;
}

function ActiveTimerLegacy() {
  const { hasTask, isRunning, title, taskId } = useActiveTimerTask();
  useSecondTick(isRunning && Boolean(taskId));

  if (!hasTask || !isRunning || !taskId) {
    return null;
  }

  const elapsedMs = useTaskTimerStore
    .getState()
    .getCurrentSegmentElapsed(taskId);

  return (
    <ActiveTimerRow
      taskId={taskId}
      title={title}
      elapsedSeconds={Math.floor(elapsedMs / 1000)}
    />
  );
}

function ActiveTimerV2() {
  const taskId = useActiveTimerV2Store((s) => s.taskId);
  const timerQuery = useQuery({
    queryKey: tasksV2Keys.timer(taskId ?? ''),
    queryFn: () => fetchTaskTimerV2(taskId as string),
    enabled: Boolean(taskId),
    // Пока таймер идёт — перечитываем: пауза или стоп с другого устройства снимут виджет.
    refetchInterval: (query) =>
      query.state.data?.running ? V2_STATUS_POLL_MS : false,
  });
  const isRunning = Boolean(timerQuery.data?.running);
  const { data: task } = useTaskV2(isRunning ? taskId : null);
  useSecondTick(isRunning);

  if (!taskId || !isRunning) {
    return null;
  }

  const sinceFetch = Math.max(
    0,
    Math.floor((Date.now() - timerQuery.dataUpdatedAt) / 1000)
  );

  return (
    <ActiveTimerRow
      taskId={taskId}
      title={task?.title?.trim() || 'Без названия'}
      elapsedSeconds={(timerQuery.data?.elapsedSeconds ?? 0) + sinceFetch}
    />
  );
}
